/**
 * Submission state — the active submission id + step pointer + status
 * flags for the "create submission" POST. `flowSubmissionId` and `step`
 * are mirrored to sessionStorage so a hard refresh on /payment,
 * /reviewDocusign, /binder-invoice (and friends) keeps the user on the
 * same page instead of bouncing back to /dashboard or the landing page.
 * Mirror clears with the tab (sessionStorage) and on `clear()`.
 */
const SS_KEY_SID  = "q2b:submission:flowSubmissionId";
const SS_KEY_STEP = "q2b:submission:step";

const readSession = (key) => {
  try { return typeof sessionStorage !== "undefined" ? sessionStorage.getItem(key) : null; }
  catch { return null; }
};
const writeSession = (key, value) => {
  try {
    if (typeof sessionStorage === "undefined") return;
    if (value == null || value === "") sessionStorage.removeItem(key);
    else sessionStorage.setItem(key, String(value));
  } catch { /* private mode / quota — fall through */ }
};

class SubmissionStore {
  #listeners = new Set();

  #flowSubmissionId = (() => {
    const raw = readSession(SS_KEY_SID);
    return raw ? raw : null;
  })();
  #step = (() => {
    const raw = readSession(SS_KEY_STEP);
    const n = raw == null ? NaN : Number(raw);
    return Number.isFinite(n) ? n : 0;
  })();

  get flowSubmissionId() { return this.#flowSubmissionId; }
  set flowSubmissionId(v) {
    this.#flowSubmissionId = v;
    writeSession(SS_KEY_SID, v);
    this.#notify();
  }

  get step() { return this.#step; }
  set step(v) {
    this.#step = Number.isFinite(v) ? v : 0;
    writeSession(SS_KEY_STEP, this.#step);
    this.#notify();
  }

  // POST /insured/submission status (auth'd "Create order" button)
  #insuredSubmissionLoading = false;
  #insuredSubmissionError = null;

  get insuredSubmissionLoading() { return this.#insuredSubmissionLoading; }
  set insuredSubmissionLoading(v) { this.#insuredSubmissionLoading = !!v; this.#notify(); }

  get insuredSubmissionError() { return this.#insuredSubmissionError; }
  set insuredSubmissionError(v) { this.#insuredSubmissionError = v; this.#notify(); }

  subscribe(listener) {
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  }

  #notify() { this.#listeners.forEach((l) => l()); }

  clear() {
    this.#flowSubmissionId = null;
    this.#step = 0;
    this.#insuredSubmissionLoading = false;
    this.#insuredSubmissionError = null;
    writeSession(SS_KEY_SID, null);
    writeSession(SS_KEY_STEP, null);
    this.#notify();
  }
}

const submissionStore = new SubmissionStore();
export default submissionStore;
